import "ress/ress.css";
import React from "react";
import { Helmet } from "react-helmet";
import { useStaticQuery, graphql } from "gatsby";
import { Header } from "~/organisms/Header";
import { Footer } from "~/organisms/Footer";
import { BreadCrumb, BreadCrumbs } from "~/organisms/BreadCrumbs";
import { InitStyle } from "~/atoms/InitStyle";
import { AskUs } from "~/molecules/IndexPageAskUs";
import ogpPng from "~/images/ogp.png";
import { buildBreadcrumbListSchema } from "~/utils/breadcrumb";
import { colors } from "~/utils/colors";
import { breadcrumbContentGap, headerHeight } from "~/utils/variables";
import "~/utils/styledJsx";

// ユーティリティ: 相対パスを絶対パスに変換する関数
const constructUrl = (baseUrl: string, path: string) => {
  if (path.startsWith("http")) return path;
  return new URL(path, baseUrl).href;
};

type Props = {
  title?: string;
  /** true のとき title をそのまま pageTitle に使い、サイト名を付与しない（記事ページなどで二重防止） */
  omitSiteName?: boolean;
  children: React.ReactNode;
  ogpDescription?: string;
  ogpImageUrl?: string;
  ogpTitle?: string;
  noindex?: boolean;
  canonicalUrl?: string;
  isTopPage?: boolean;
  hideBreadCrumbs?: boolean;
  breadCrumbItems?: BreadCrumb[];
  breadCrumbBgColor?: string;
  breadCrumbSpPaddingTop?: string;
  breadCrumbSpPaddingBottom?: string;
};

export const Layout: React.FC<Props> = ({
  title,
  omitSiteName = false,
  children,
  ogpDescription,
  ogpImageUrl,
  ogpTitle,
  noindex,
  canonicalUrl,
  isTopPage = false,
  hideBreadCrumbs = false,
  breadCrumbItems,
  breadCrumbBgColor,
  breadCrumbSpPaddingTop,
  breadCrumbSpPaddingBottom,
}) => {
  const { site } = useStaticQuery(graphql`
    query {
      site {
        siteMetadata {
          title
          description
          author
          siteUrl
        }
      }
    }
  `);

  const pageTitle = omitSiteName && title
    ? title
    : title
      ? `${title}｜${site.siteMetadata.title}`
      : site.siteMetadata.title;
  const sharedOgpTitle = ogpTitle ? ogpTitle : pageTitle;
  const sharedOgpDescription = ogpDescription || site.siteMetadata.description;

  // ★修正1: OGP画像を絶対パス化（siteUrlと結合）
  const ogpImagePath = ogpImageUrl || ogpPng;
  const absoluteOgpImageUrl = constructUrl(site.siteMetadata.siteUrl, ogpImagePath);

  const breadcrumbSchema =
    breadCrumbItems && breadCrumbItems.length > 0
      ? buildBreadcrumbListSchema(site.siteMetadata.siteUrl, breadCrumbItems, canonicalUrl)
      : null;

  // Organization構造化データ（Schema.org）
  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "株式会社ウィルオブ・パートナー",
    url: "https://willof-partner.co.jp/",
    logo: "https://willof-partner.co.jp/static/d08faf3fc06ce56f5d53f2d5f55e308d/fae8d/logo.webp",
    sameAs: ["https://jp.refcome.com/insight"],
    contactPoint: {
      "@type": "ContactPoint",
      telephone: "+81-3-5656-9966",
      contactType: "customer service",
    },
  };

  const meta = [
    {
      name: `description`,
      content: sharedOgpDescription,
    },
    {
      property: `og:title`,
      content: sharedOgpTitle,
    },
    {
      property: `og:description`,
      content: sharedOgpDescription,
    },
    {
      property: `og:type`,
      content: `website`,
    },
    {
      property: `og:image`,
      content: absoluteOgpImageUrl, // ★絶対パスを使用
    },
    {
      property: `fb:app_id`,
      content: `2220166827995808`,
    },
    {
      name: `twitter:card`,
      content: `summary_large_image`,
    },
    {
      name: `twitter:creator`,
      content: site.siteMetadata.author,
    },
    {
      name: `twitter:title`,
      content: sharedOgpTitle,
    },
    {
      name: `twitter:description`,
      content: sharedOgpDescription,
    },
    {
      name: `twitter:image`,
      content: absoluteOgpImageUrl, // ★絶対パスを使用
    },
    {
      httpEquiv: `content-language`,
      content: "ja",
    },
    // ★修正2: 条件付きmetaタグを安全に追加
    ...(noindex ? [{ name: `robots`, content: `noindex` }] : []),
  ];

  const hasBreadCrumbs = !isTopPage && !hideBreadCrumbs && Boolean(breadCrumbItems?.length);
  const contentRootBgColor = hasBreadCrumbs ? breadCrumbBgColor || colors.white : undefined;

  return (
    <>
      <style jsx>{`
        .contentRoot {
          margin-top: 0;
        }
        @media (max-width: 767px) {
          .contentRoot {
            margin-top: 0;
          }
        }
        .contentRoot.withHeaderOffset {
          margin-top: ${headerHeight.desktop}px;
        }
        @media (max-width: 767px) {
          .contentRoot.withHeaderOffset {
            margin-top: ${headerHeight.mobile}px;
          }
        }
        .contentRoot.withBreadcrumbs {
          background-color: ${contentRootBgColor || colors.white};
        }
        .contentRoot.withBreadcrumbs main {
          padding-top: ${breadcrumbContentGap}px;
        }
        @media (max-height: 800px) {
          .contentRoot.withBreadcrumbs main {
            padding-top: 16px;
          }
        }
      `}</style>
      <Helmet
        htmlAttributes={{
          lang: "ja",
        }}
        meta={meta}
      >
        <title>{pageTitle}</title>
        {canonicalUrl && <link rel="canonical" href={canonicalUrl} />}
        {/* Schema.orgの埋め込み */}
        <script type="application/ld+json">{JSON.stringify(organizationSchema)}</script>
        {breadcrumbSchema && (
          <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
        )}
      </Helmet>
      <InitStyle />
      <Header isTopPage={isTopPage} />
      <div
        className={`contentRoot ${!isTopPage ? "withHeaderOffset" : ""} ${hasBreadCrumbs ? "withBreadcrumbs" : ""}`}
      >
        {hasBreadCrumbs && (
          <BreadCrumbs
            items={breadCrumbItems!}
            bgColor={breadCrumbBgColor}
            spPaddingTop={breadCrumbSpPaddingTop}
            spPaddingBottom={breadCrumbSpPaddingBottom}
          />
        )}
        <main>{children}</main>
      </div>
      <AskUs />
      <Footer />
    </>
  );
};
